import styled from '@emotion/styled';
import { IconButton, TextField } from '@mui/material';
import { Icons } from 'components';
import { COLORS } from 'styles/constants';
import { osName } from 'react-device-detect';
import { useState } from 'react';
import { hideScroll } from 'styles/utils';

interface ShortcutProps {
  onClose?: () => void;
}

const Shortcut = ({ onClose }: ShortcutProps) => {
  const os = osName;
  const shortcutList = [
    [
      { title: '기본 선택', shortcut: ['클릭'] },
      { title: '태스크박스 빠른 선택', shortcut: ['1', '~', '9'] },
      { title: '태스크박스 선택 이동', shortcut: ['↑', ',', '↓'] },
      { title: '우측 플랜 탭 보기', shortcut: os === 'Windows' ? ['Alt', '+', '1'] : ['Option', '+', '1'] },
      { title: '우측 메모 탭 보기', shortcut: os === 'Windows' ? ['Alt', '+', '2'] : ['Option', '+', '2'] },
      { title: '우측 나중에 탭 보기', shortcut: os === 'Windows' ? ['Alt', '+', '3'] : ['Option', '+', '3'] },
      { title: '실행뷰 전환(일간 / 주간)', shortcut: ['D', '/', 'W'] },
      { title: '오늘 일지 보기', shortcut: ['Shift', '+', 'T'] },
      { title: '하위업무 접기 펴기', shortcut: os === 'Windows' ? ['Alt', '+', 'F'] : ['Option', '+', 'F'] },
    ],
    [
      {
        title: '태스크 다중선택',
        shortcut: os === 'Windows' ? ['Crtl', '+', '클릭'] : ['Cmd', '+', '클릭'],
      },
      { title: '태스크 연속선택', shortcut: ['다중선택', '+', 'Shift', '+', '↑', ',', '↓'] },
      { title: '그룹으로 합치기', shortcut: ['다중선택', '+', 'G'] },
      {
        title: '삭제',
        shortcut: os === 'Windows' ? ['Delete'] : ['Cmd', '+', '←'],
        shortcutWithEdit: os === 'Windows' ? ['Shift', '+', 'Delete'] : ['Shift', '+', 'Cmd', '+', '←'],
      },
    ],
    [
      { title: '오늘 할 일 입력하기', shortcut: ['/'] },
      {
        title: '태스크 완료하기',
        shortcut: ['선택', '+', 'V'],
        shortcutWithEdit: os === 'Windows' ? ['Alt', '+', 'Shift', '+', 'V'] : ['Option', '+', 'Shift', '+', 'V'],
      },
      { title: '일지 접기 펴기', shortcut: os === 'Windows' ? ['Alt', '+', 'F'] : ['Option', '+', 'F'] },
      {
        title: '태스크박스 포커스모드(열기, 최소화)',
        shortcut: ['선택', '+', 'F'],
        shortcutWithEdit: os === 'Windows' ? ['Alt', '+', 'Shift', '+', 'F'] : ['Option', '+', 'Shift', '+', 'F'],
      },
      {
        title: '태스크박스 시간 및 날짜 추가/수정',
        shortcut: ['선택', '+', 'T'],
        shortcutWithEdit: os === 'Windows' ? ['Alt', '+', 'Shift', '+', 'T'] : ['Option', '+', 'Shift', '+', 'T'],
      },
      {
        title: '태스크박스 다음날로 보내기',
        shortcut: ['T', '그리고', 'N'],
        shortcutWithEdit: os === 'Windows' ? ['Alt', '+', 'Shift', '+', 'T', '그리고', 'N'] : ['Option', '+', 'Shift', '+', 'T', '그리고', 'N'],
      },
      {
        title: '태스크박스 다음주 월요일로 보내기',
        shortcut: ['T', '그리고', 'M'],
        shortcutWithEdit: os === 'Windows' ? ['Alt', '+', 'Shift', '+', 'T', '그리고', 'M'] : ['Option', '+', 'Shift', '+', 'T', '그리고', 'M'],
      },
      {
        title: '태스크박스 나중에로 보내기',
        shortcut: ['T', '그리고', 'L'],
        shortcutWithEdit: os === 'Windows' ? ['Alt', '+', 'Shift', '+', 'T', '그리고', 'L'] : ['Option', '+', 'Shift', '+', 'T', '그리고', 'L'],
      },
      {
        title: '하위업무 나중에로 보내기',
        shortcut: ['L'],
        shortcutWithEdit: os === 'Windows' ? ['Alt', '+', 'Shift', '+', 'L'] : ['Option', '+', 'Shift', '+', 'L'],
      },
    ],
    [{ title: '나중에 태스크 입력하기', shortcut: os === 'Windows' ? ['Ctrl', '+', '/'] : ['Cmd', '+', '/'] }],
  ];

  const [filteredShortcutList, setFilteredShortcutList] = useState<({ title: string; shortcut: string[] } | { title: string; shortcut: string[][] })[][]>();

  const handleFilterShortcut = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) {
      setFilteredShortcutList([]);
    } else {
      const filteredShortcutList = shortcutList.map((shortcut) => {
        return shortcut.filter((item) => item.title.toLowerCase().startsWith(value));
      });

      setFilteredShortcutList(filteredShortcutList);
    }
  };

  return (
    <Container>
      <Header>
        <div>단축키</div>
        <IconButton onClick={onClose} sx={{ 'padding': '0px', ':hover': { backgroundColor: COLORS.white } }}>
          <Icons.Close width={20} height={20} />
        </IconButton>
      </Header>
      <SearchWrapper>
        <TextField
          fullWidth
          placeholder="검색"
          onChange={handleFilterShortcut}
          InputProps={{
            sx: { height: '36px', fontSize: '13px', borderRadius: '8px' },
            startAdornment: (
              <div style={{ marginRight: '4px' }}>
                <Icons.Search width={16} height={16} />
              </div>
            ),
          }}
        />
        <SearchTextWrapper>
          <Icons.ShortcutWithText />
          <div style={{ margin: '0px 4px' }}>
            <Icons.ArrowRightSmall fill={COLORS.gray500} />
          </div>
          <div style={{ fontSize: '12px' }}>텍스트 편집 상태에서도 단축키 사용이 가능해요.</div>
        </SearchTextWrapper>
      </SearchWrapper>
      <ShortcutWrapper>
        {filteredShortcutList?.map((v) => v.length > 0).some((v) => v)
          ? filteredShortcutList!.map((shortcut, index) => (
              <>
                {shortcut.length > 0 && (
                  <div style={{ marginTop: '24px' }} key={index}>
                    <ShortcutTitleWrapper>
                      <div style={{ fontSize: '16px', fontWeight: 700, marginRight: '8px' }}>{index === 0 ? '기본' : '태스크'}</div>
                      <div style={{ fontSize: '13px', color: COLORS.gray500 }}>
                        {index === 1 ? '공통' : index === 2 ? '실행메뉴 일지 태스크' : index === 3 ? '우측 나중에 탭 태스크' : ''}
                      </div>
                    </ShortcutTitleWrapper>
                    <ShortcutContentWrapper>
                      {shortcut.map((item, index) => (
                        <div style={{ padding: '8px', borderBottom: index === shortcut.length - 1 ? 'none' : `1px solid ${COLORS.gray100}` }}>
                          <div>{item.title}</div>
                        </div>
                      ))}
                    </ShortcutContentWrapper>
                  </div>
                )}
              </>
            ))
          : shortcutList.map((shortcut, index) => (
              <div style={{ marginTop: '24px' }} key={index}>
                <ShortcutTitleWrapper>
                  <div style={{ fontSize: '16px', fontWeight: 700, marginRight: '8px' }}>{index === 0 ? '기본' : '태스크'}</div>
                  <div style={{ fontSize: '13px', color: COLORS.gray500 }}>
                    {index === 1 ? '공통' : index === 2 ? '실행메뉴 일지 태스크' : index === 3 ? '우측 나중에 탭 태스크' : ''}
                  </div>
                </ShortcutTitleWrapper>
                <ShortcutContentWrapper>
                  {shortcut.map((item, index) => (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        padding: '8px',
                        borderBottom: index === shortcut.length - 1 ? 'none' : `1px solid ${COLORS.gray100}`,
                      }}
                      key={index}
                    >
                      <ShortcutTitle>{item.title}</ShortcutTitle>
                      <ShortcutDescription>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {item.shortcut.map((v) => (
                            <>
                              {(shortcutList.findIndex((v) => v === shortcut) === 0 && v === '/') ||
                              v === ',' ||
                              v === 'or' ||
                              v === '+' ||
                              v === '그리고' ||
                              v === '~' ? (
                                <div style={{ marginLeft: '4px' }}>{v}</div>
                              ) : (
                                <KeyboardButtonRect>{v}</KeyboardButtonRect>
                              )}
                            </>
                          ))}
                        </div>

                        {item.shortcutWithEdit && (
                          <div style={{ display: 'flex', alignItems: 'center', marginTop: '6px' }}>
                            <Icons.ShortcutWithText />
                            {item.shortcutWithEdit &&
                              item.shortcutWithEdit.map((v) => (
                                <>
                                  {v === ',' || v === 'or' || v === '+' || v === '그리고' ? (
                                    <div style={{ marginLeft: '4px' }}>{v}</div>
                                  ) : (
                                    <KeyboardButtonRect>{v}</KeyboardButtonRect>
                                  )}
                                </>
                              ))}
                          </div>
                        )}
                      </ShortcutDescription>
                    </div>
                  ))}
                </ShortcutContentWrapper>
              </div>
            ))}
      </ShortcutWrapper>
    </Container>
  );
};

export default Shortcut;

const Container = styled.div`
  width: 462px;
  padding: 24px;
  overflow-y: scroll;
  ${hideScroll()}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  font-weight: 700;
`;

const SearchWrapper = styled.div`
  margin-top: 8px;
`;

const SearchTextWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
  padding-left: 8px;
`;

const ShortcutWrapper = styled.div``;

const ShortcutTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const ShortcutContentWrapper = styled.div`
  border: 1px solid ${COLORS.gray200};
  border-radius: 8px;
  font-size: 12px;
  padding: 4px 8px;
`;

const ShortcutTitle = styled.div`
  margin-top: 3px;
`;

const ShortcutDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: ${COLORS.gray500};
`;

const KeyboardButtonRect = styled.div`
  font-size: 10px;
  border-radius: 4px;
  background-color: ${COLORS.gray100};
  padding: 4px 6px;
  margin-left: 4px;
`;
